import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { ShoppingCart, FileText, Download, Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface AdminOrder {
    id: string;
    userName: string;
    email?: string;
    phone?: string;
    planName: string;
    amount: number;
    status: string;
    created_at: string;
}

const AdminOrders = () => {
    const [orders, setOrders] = useState<AdminOrder[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadOrders = async () => {
            setIsLoading(true);
            const { data, error } = await supabase
                .from('payments')
                .select('*, courses(title)')
                .order('created_at', { ascending: false });

            if (data) {
                setOrders(data.map((o: any) => ({
                    id: o.id.slice(0, 8).toUpperCase(),
                    userName: o.name || 'Anonymous Student',
                    email: o.email,
                    phone: o.phone,
                    planName: o.courses?.title || 'Course Access',
                    amount: o.amount,
                    status: o.status,
                    created_at: o.created_at
                })));
            }
            setIsLoading(false);
        }
        loadOrders();
    }, []);


    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Payments / Orders</h2>
                        <p className="text-slate-500 font-medium text-sm">View all course purchases and payments here.</p>
                    </div>
                </div>

                <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px] md:min-w-0">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Order ID</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Student Info</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Course Plan</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Amount</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                    <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {orders.length > 0 ? orders.map((order) => (
                                    <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6 text-[10px] font-black text-slate-900">{order.id}</td>
                                        <td className="px-8 py-6">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-black text-slate-900 uppercase tracking-tight">{order.userName}</span>
                                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{order.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">{order.planName}</span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-[10px] font-black text-slate-900">₹{order.amount.toLocaleString()}</span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${order.status === 'success' || order.status === 'paid'
                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                : order.status === 'pending'
                                                    ? 'bg-amber-50 text-amber-600 border-amber-100'
                                                    : order.status === 'failed'
                                                        ? 'bg-red-50 text-red-600 border-red-100'
                                                        : 'bg-slate-50 text-slate-400 border-slate-100'
                                                }`}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                            {new Date(order.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={6} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">No orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminOrders;
